import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import DvConstraintsForm from './DvConstraintsForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { dvConstraintsSchema } from 'validationSchema';
import { DvConstraintsFormType, ParamType } from 'interfaces';
import { flexCenterBetween } from 'styles/commonComponentStyle';
import {
  DailyValueConstraintPayload,
  DailyValueConstraintUnit,
  useGetDvConstraintLazyQuery,
  useUpdateDvConstraintMutation,
} from 'generated/graphql';
import {
  HTTP_STATUS,
  EDIT_DV_CONSTRAINTS_TEXT,
  dvConstraintsInitialValue,
  DV_CONSTRAINTS_IS_UPDATED_TEXT,
} from 'constants/index';

const Edit: FC = (): JSX.Element => {
  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const methods = useForm<DvConstraintsFormType>({
    defaultValues: dvConstraintsInitialValue,
    resolver: yupResolver(dvConstraintsSchema),
  });

  const { handleSubmit, setValue } = methods;

  const [updateDvConstraint, { loading: dvConstraintLoading }] = useUpdateDvConstraintMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateDailyValueConstraint } = data;
      const { response } = updateDailyValueConstraint || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getDvConstraint, { loading: getLoading }] = useGetDvConstraintLazyQuery({
    onCompleted: (data) => {
      const { getDailyValueConstraint } = data || {};
      const { response, dailyValueConstraint } = getDailyValueConstraint || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(dailyValueConstraint as DailyValueConstraintPayload['dailyValueConstraint']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onSubmit: SubmitHandler<DvConstraintsFormType> = async (data) => {
    const { name, unit, infants, adultsAndChildren, children1To3Years, pregnantAndLactatingWomen } =
      data || {};

    await updateDvConstraint({
      variables: {
        updateDailyValueConstraintInput: {
          id: id || '',
          name,
          unit: unit as DailyValueConstraintUnit,
          infants: Number(infants),
          adultsAndChildren: Number(adultsAndChildren),
          children1To3Years: Number(children1To3Years),
          pregnantAndLactatingWomen: Number(pregnantAndLactatingWomen),
        },
      },
    });
  };

  const fetchDvConstraint = useCallback(async () => {
    id &&
      (await getDvConstraint({
        variables: {
          getDailyValueConstraintInput: {
            id,
          },
        },
      }));
  }, [id, getDvConstraint]);

  const setValues = (params: DailyValueConstraintPayload['dailyValueConstraint']) => {
    const {
      name,
      foodComponent,
      unit,
      infants,
      adultsAndChildren,
      children1To3Years,
      pregnantAndLactatingWomen,
    } = params || {};
    setValue('name', name || '');
    setValue('foodComponent', foodComponent || '');
    setValue('unit', unit || '');
    setValue('infants', `${infants || ''}`);
    setValue('children1To3Years', `${children1To3Years || ''}`);
    setValue('adultsAndChildren', `${adultsAndChildren || ''}`);
    setValue('pregnantAndLactatingWomen', `${pregnantAndLactatingWomen || ''}`);
  };

  useEffect(() => {
    id && fetchDvConstraint();
  }, [id, fetchDvConstraint]);

  const loading = dvConstraintLoading || getLoading;

  return (
    <Fragment>
      <Box sx={flexCenterBetween}>
        <Typography variant="h5">{EDIT_DV_CONSTRAINTS_TEXT}</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item lg={6} md={12}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DvConstraintsForm loading={loading} isEdit />
            </form>
          </FormProvider>
        </Grid>
      </Grid>

      <BackdropLoader open={loading} text={DV_CONSTRAINTS_IS_UPDATED_TEXT} />
    </Fragment>
  );
};

export default Edit;
